import { Signal } from 'typed-signals';
import { getLocalizedText } from '@softgames/localization';
import type { Popup } from '../../../../popups/Popup';
import { WinScreenLayout } from '../../../../../layouts/winScreen/WinScreenLayout';
import { applyLayoutState } from '../../../../libs/layouts/populateLayouts/applyLayoutState';
import { RewardType, WinPopupRewardView } from './WinPopupRewardView';
import { centerElements } from '../../../utils/centerElements';
import { playLayoutAnimation } from '../../../../libs/layouts/layoutAnimations/playLayoutAnimation';
import { PiggyBankIconView } from '../../../../gameEvents/piggyBank/views/PiggyBankIconView';
import { animateCoinFlySequence } from '../../common/animateCoinFlySequence';
import type { CoinCounterView } from '../../common/counters/CoinCounterView';
import { soundManager } from '../../../../app/SoundManager';
import type { PiggyBankTier } from '../../../../gameEvents/piggyBank/types';

export interface NewWinPopupData {
  readonly level: number;
  readonly reward: number;
  readonly coinCounter: CoinCounterView;
  readonly dioramaReward: number;
  readonly doubleRewardAvailable: boolean;
  readonly piggyBank: { tier: PiggyBankTier, initBalance: number; balance: number; maxCoins: number; reward: number } | null;
}

export class NewWinPopup implements Popup {
  public readonly onDoubleCoinsClicked = new Signal();

  public readonly onNextClicked = new Signal();

  private readonly layout = new WinScreenLayout();

  public readonly root = this.layout.root;

  private readonly rewardsViews: WinPopupRewardView[] = [];

  private coinRewardView: WinPopupRewardView | null = null;

  private piggyRewardView: WinPopupRewardView | null = null;

  private piggyBankIcon: PiggyBankIconView | null = null;

  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  private readonly appearAnimation = Promise.withResolvers<void>();

  constructor(private readonly config: NewWinPopupData) {
    this.init();
  }

  public async onAdded(): Promise<void> {
    await playLayoutAnimation(this.layout, 'Win_Screen_Appear', false, async (eventName) => {
      if (eventName === 'startAppearRewards') {
        this.startRewardsAppearAnimation();
      }
    });

    this.appearAnimation.resolve();
  }

  private init(): void {
    this.initRewards();

    this.initPiggyBank();

    this.layout.nextButton.root.on('pointertap', () => {
      this.onNextClicked.emit();
    });

    this.layout.doubleCoins.root.on('pointertap', () => {
      this.onDoubleCoinsClicked.emit();
    });

    this.layout.levelXCompleteText.text = getLocalizedText('Level <nr> complete!').replace(
      '<nr>',
      `${this.config.level}`,
    );

    if (this.config.reward === 0 || !this.config.doubleRewardAvailable) {
      applyLayoutState(this.layout, 'noDoubleCoins');
    }
  }

  public async animateDoubleReward(amount: number): Promise<void> {
    const {
      coinRewardView,
      config: { coinCounter },
    } = this;

    void playLayoutAnimation(this.layout, 'Hide_Double_Coins');

    await this.appearAnimation.promise;

    if (coinRewardView && coinCounter) {
      void coinRewardView.displayOldAmountAnimation(this.config.reward);
      coinRewardView.setAmountText(amount);
    }
  }

  private initPiggyBank(): void {
    const { piggyBank } = this.config;
    // Don't show the piggy bank icon if it's already full
    if (!piggyBank || piggyBank.initBalance === piggyBank.balance) return;

    this.piggyBankIcon = new PiggyBankIconView()
      .props({
        parent: this.layout.piggyBankIconSlot,
      })
      .updateProgress(piggyBank.initBalance, piggyBank.maxCoins, true);

    this.piggyBankIcon.refreshState(piggyBank.tier, null);
  }

  private initRewards(): void {
    const { reward, piggyBank, dioramaReward } = this.config;
    const { rewardsSlot } = this.layout;

    if (reward > 0) {
      this.coinRewardView = new WinPopupRewardView({
        type: RewardType.Coins,
        amount: reward,
      }).props({
        parent: rewardsSlot,
      });
      this.rewardsViews.push(this.coinRewardView);
    }

    if (dioramaReward > 0) {
      this.rewardsViews.push(
        new WinPopupRewardView({
          type: RewardType.Tiles,
          amount: dioramaReward,
        }).props({
          parent: rewardsSlot,
        }),
      );
    }

    if (piggyBank) {
      this.piggyRewardView = new WinPopupRewardView({
        type: RewardType.PiggyBank,
        amount: piggyBank.reward,
      }).props({
        parent: rewardsSlot,
      });
      this.piggyRewardView.setPiggyBankTier(piggyBank.tier);
      this.rewardsViews.push(this.piggyRewardView);
    }

    centerElements(rewardsSlot.children, 250);
  }

  private startRewardsAppearAnimation(): void {
    this.rewardsViews.forEach(async (view, index) => {
      await this.root.wait(index * 50);
      void view.playAppearAnimation();
    });
  }

  public async playCollectRewardAnimation(): Promise<void> {
    const promises: Promise<void>[] = [];

    await playLayoutAnimation(this.layout, 'Apply_Rewards', false, async (eventName) => {
      const { coinParticleSlot } = this.layout;

      if (eventName === 'startRewardCoins') {
        const {
          coinRewardView,
          config: { coinCounter },
        } = this;

        if (coinRewardView && coinCounter) {

          soundManager.play('Gameplay_Coin_Reward');

          promises.push(
            animateCoinFlySequence({
              startPoint: coinParticleSlot.toLocal(coinRewardView.getGlobalPartStartPoint()),
              targetPoint: coinParticleSlot.toLocal(this.config.coinCounter.getIconGlobalPosition()),
              particleParent: coinParticleSlot,
              coinCount: coinRewardView.getDisplayedAmount(),
              onCoinFlyStart(coinsFlown) {
                coinRewardView.setAmountText(coinRewardView.getDisplayedAmount() - coinsFlown);
              },
              onCoinArrived: (coinsFlown) => {
                coinCounter.updateEnabled = true;
                coinCounter.updateAmount(coinCounter.getDisplayedAmount() + coinsFlown);
                coinCounter.updateEnabled = false;
              },
              isCancelled: () => this.root.destroyed,
            }),
          );
        }
      } else if (eventName === 'startPiggyCoins') {
        const {
          piggyBankIcon,
          piggyRewardView,
          config: { piggyBank },
        } = this;
        if (piggyRewardView && piggyBank) {
          if (piggyBank.initBalance === piggyBank.balance) {
            piggyRewardView.setType(RewardType.PiggyBankFull);
            void piggyRewardView.playPiggyBankFullAnimation();
          } else if (piggyBankIcon) {
            let amount = piggyBank.initBalance;
            promises.push(
              animateCoinFlySequence({
                startPoint: coinParticleSlot.toLocal(piggyRewardView.getGlobalPartStartPoint()),
                targetPoint: coinParticleSlot.toLocal(piggyBankIcon.getGlobalPosition()),
                particleParent: coinParticleSlot,
                coinCount: piggyRewardView.getDisplayedAmount(),
                unitsPerParticle: piggyRewardView.getDisplayedAmount() / 30,
                maxIntervalMs: 20,
                onCoinFlyStart: (coinsFlown) => {
                  piggyRewardView.setAmountText(piggyRewardView.getDisplayedAmount() - coinsFlown);
                },
                onCoinArrived: (coinsFlown) => {
                  amount += coinsFlown;
                  piggyBankIcon.updateProgress(Math.min(amount, piggyBank.maxCoins), piggyBank.maxCoins);
                },
                isCancelled: () => this.root.destroyed,
              }),
            );
          }
        }
      }
    });

    await Promise.all(promises);
  }
}
